import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UserPlus, Mail, Lock, Loader2 } from "lucide-react";
import { base44 } from "@/api/base44Client";
import { Button } from "@/components/ui/button";
import AuthLayout from "@/components/AuthLayout";
import { IconField, FormAlert } from "@/components/auth/IconField";
import { AccessCodeStep } from "@/components/auth/AccessCodeStep";
import { isNative } from "@/lib/native";

export default function Register() {
  const { t } = useTranslation();
  const [accessCode, setAccessCode] = useState("");
  const [accessCodeValid, setAccessCodeValid] = useState(isNative());
  const [accessCodeError, setAccessCodeError] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleCodeSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      await base44.auth.validateCode(accessCode);
      setAccessCodeValid(true);
      setAccessCodeError("");
    } catch {
      setAccessCodeError(t("register.codeInvalid"));
    } finally { setLoading(false); }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    if (password !== confirmPassword) {
      setError(t("register.passwordsDontMatch"));
      return;
    }
    setLoading(true);
    try {
      await base44.auth.register({ email, password, accessCode });
      window.location.href = "/";
    } catch (failure) {
      setError(failure.message || t("register.createError"));
    } finally { setLoading(false); }
  };

  if (!accessCodeValid) return <AccessCodeStep accessCode={accessCode} onChange={setAccessCode}
    onSubmit={handleCodeSubmit} loading={loading} error={accessCodeError} />;

  return <AuthLayout
    icon={UserPlus}
    title={t("register.title")}
    subtitle={t("register.subtitle")}
    footer={<>{t("register.alreadyHaveAccount")}{" "}
      <Link to="/login" data-testid="register-login" className="text-primary font-medium hover:underline">{t("register.signIn")}</Link>
    </>}>
    <div className="mb-4"><FormAlert message={error} testId="register-error" /></div>
    <form onSubmit={handleSubmit} className="space-y-4">
      <IconField id="email" icon={Mail} label={t("common.email")} data-testid="register-email-input"
        type="email" autoComplete="email" autoFocus placeholder="seu@email.com" required
        value={email} onChange={(event) => setEmail(event.target.value)} />
      <IconField id="password" icon={Lock} label={t("common.password")} data-testid="register-password-input"
        type="password" autoComplete="new-password" placeholder="••••••••" required
        value={password} onChange={(event) => setPassword(event.target.value)} />
      <IconField id="confirm" icon={Lock} label={t("common.confirmPassword")} data-testid="register-confirm-input"
        type="password" autoComplete="new-password" placeholder="••••••••" required
        value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} />
      <Button type="submit" data-testid="register-submit" className="w-full h-12 font-medium" disabled={loading}>
        {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
        {loading ? t("register.submitting") : t("register.submit")}
      </Button>
    </form>
  </AuthLayout>;
}
